import { useCallback, useEffect, useState } from 'react'

/** True when the keystroke belongs to whatever the user is typing into. */
function isEditing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.closest('[role="dialog"]') !== null
  )
}

/**
 * A single-key shortcut, Jira-style: `c` creates an issue.
 *
 * It deliberately ignores the key while a field has focus, while a dialog is
 * open, and whenever a modifier is held — otherwise ⌘C would open a dialog
 * instead of copying, which is the kind of thing that makes people stop
 * trusting a keyboard shortcut entirely.
 */
export function useHotkey(key: string, handler: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key.toLowerCase() !== key.toLowerCase()) return
      if (isEditing(event.target)) return
      event.preventDefault()
      handler()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [key, handler, enabled])
}

export interface Disclosure {
  isOpen: boolean
  open: () => void
  close: () => void
}

export function useDisclosure(initial = false): Disclosure {
  const [isOpen, setIsOpen] = useState(initial)
  return {
    isOpen,
    open: useCallback(() => setIsOpen(true), []),
    close: useCallback(() => setIsOpen(false), []),
  }
}
